
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { AbandonedTrip } from '@/api/entities';
import { User } from '@/api/entities';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Search, Clock, Plane, AlertCircle, ChevronLeft, ChevronRight, ExternalLink, Copy, Check, Send, TrendingUp, Eye, Users as UsersIcon, CreditCard, RefreshCw, AlertTriangle, ShieldAlert, Server, HelpCircle, Mail, Phone } from 'lucide-react';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import { getCityName } from '../components/airportUtils';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { createPageUrl, calculateServiceFee } from '@/utils';
import { DEFERA_CAPITAL_HOLD_FEE_RATE, SLICE_PAY_HOLD_FEE_RATE } from '@/utils/paymentScheduleCalculator';
import { useInstallmentsCopy } from '@/hooks/useInstallmentsCopy';
import { useInstallmentsCheckoutProvider } from '@/hooks/useInstallmentsCheckoutProvider';
import SectionBand from '@/components/ui/SectionBand';
import {
  BRAND_LIST_DIVIDER,
  CART_SECTION_CARD,
  DASHBOARD_FILTER_INPUT,
  DASHBOARD_TAB_ACTIVE,
  DASHBOARD_TAB_INACTIVE,
} from '@/constants/brandColors';
import PaymentProcessorBadge from '@/components/defera-capital-nmi/PaymentProcessorBadge';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';
import VipBadge from '@/components/admin/VipBadge';

const cartLogoShell =
  'w-11 h-11 rounded-xl bg-brand-900/40 border border-brand-800/45 flex items-center justify-center flex-shrink-0 p-1.5';

const cartMetaChip =
  'text-xs text-white/75 font-normal bg-brand-900/30 border border-brand-800/40 rounded-lg px-3 py-1.5 inline-block';

export default function CartsPage() {
  const ic = useInstallmentsCopy();
  const installmentsCheckoutProvider = useInstallmentsCheckoutProvider();
  const isDeferaCapitalEffective = (cart) => {
    const pm = cart.payment_method || cart.flight_data?._payment_method || cart.flight_data?.payment_method || 'installments';
    return (
      pm === 'defera_capital' ||
      ((pm === 'installments' || pm === 'miles_installments') && installmentsCheckoutProvider === 'defera_capital')
    );
  };
  const [carts, setCarts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [userMap, setUserMap] = useState(new Map());
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedId, setCopiedId] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [stageCounts, setStageCounts] = useState({ review: 0, passenger_details: 0, payment_initiated: 0 });
  const [readyForPushCarts, setReadyForPushCarts] = useState([]);
  const ITEMS_PER_PAGE = 40;

  // Carts view: 'in_cart' (default — pre-checkout abandoned trips) or
  // 'abandoned' (Defera Capital silent abandons we surfaced after the
  // 2026-05-18 Shaurene/Ceeyana investigation). The Abandoned tab is
  // a separate dataset (loaded on demand from /api/admin/abandoned-checkouts)
  // so it doesn't bloat the In-Cart query.
  const [view, setView] = useState('in_cart');
  const [abandonedData, setAbandonedData] = useState(null); // { summary, cases }
  const [abandonedLoading, setAbandonedLoading] = useState(false);
  const [abandonedError, setAbandonedError] = useState(null);
  const [abandonedWindowDays, setAbandonedWindowDays] = useState(30);
  const [abandonedClassification, setAbandonedClassification] = useState('hard'); // 'hard' | 'soft' | 'all'
  const [abandonedBucket, setAbandonedBucket] = useState('all'); // 'all' | 'customer' | 'stripe' | 'system' | 'unknown'
  
  // Refs to avoid stale closures inside realtime callbacks
  const currentPageRef = useRef(currentPage);
  const searchTermRef = useRef(searchTerm);
  const realtimeRefreshTimeoutRef = useRef(null);
  
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);
  
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  // Fetch carts for current page (with optional search)
  const loadCarts = useCallback(async (page = 1, search = '', options = {}) => {
    const { background = false } = options;
    try {
      // IMPORTANT: Avoid full-page reload flicker on realtime updates
      // - initial/page navigations can use isLoading
      // - realtime refreshes should be background-only
      if (background) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      
      // Get session from Supabase
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }

      // Fetch paginated abandoned trips via backend admin endpoint
      const response = await fetch('/api/admin/abandoned-trips', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify({
          page,
          limit: ITEMS_PER_PAGE,
          searchTerm: search,
          excludeStatus: 'ready_for_push'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch abandoned trips: ${response.status}`);
      }

      const { trips, total, totalPages: pages, totalValue: value, stageCounts: counts } = await response.json();
      const allCarts = (trips || []).map(t => new AbandonedTrip(t));
      setCarts(allCarts.filter(cart => cart.status !== 'ready_for_push'));
      setTotalCount(total);
      setTotalPages(pages);
      setTotalValue(value || 0);
      setStageCounts(counts || { review: 0, passenger_details: 0, payment_initiated: 0 });
      
    } catch (error) {
      console.error('Error loading carts data:', error);
      console.error('Error details:', error.message, error.stack);
    } finally {
      if (!background) {
        setIsLoading(false);
      }
      setIsRefreshing(false);
    }
  }, []);

  // Fetch ready_for_push carts (miles bookings awaiting admin push)
  const loadReadyForPushCarts = useCallback(async (options = {}) => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch('/api/admin/abandoned-trips', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify({
          page: 1,
          limit: 50,
          statusFilter: 'ready_for_push'
        })
      });

      if (!response.ok) return;
      const { trips } = await response.json();
      const readyCarts = (trips || [])
        .map(t => new AbandonedTrip(t))
        .filter(cart => cart.status === 'ready_for_push');
      setReadyForPushCarts(readyCarts);
    } catch (error) {
      console.error('Error loading ready-for-push carts:', error);
    }
  }, []);

  // Defera Capital silent-abandon detector — powers the "Abandoned" tab.
  // Same logic as scripts/audit-abandoned-checkouts.mjs, just behind admin
  // auth. Loaded on-demand the first time the tab is opened (and when the
  // window changes), since it makes Stripe API calls.
  const loadAbandonedCheckouts = useCallback(async (days = 30, options = {}) => {
    const { background = false } = options;
    try {
      if (!background) setAbandonedLoading(true);
      setAbandonedError(null);

      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const response = await fetch('/api/admin/abandoned-checkouts', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({ days, graceMinutes: 30 }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error || `Failed (${response.status})`);
      }
      const data = await response.json();
      setAbandonedData(data);
    } catch (error) {
      console.error('Error loading abandoned checkouts:', error);
      setAbandonedError(error.message || 'Failed to load abandoned checkouts');
    } finally {
      if (!background) setAbandonedLoading(false);
    }
  }, []);

  // We need to read the latest `abandonedData` inside the load effect (to
  // decide between a full-spinner load vs. a background refresh) without
  // re-triggering the effect every time data changes. A ref gives us a stable
  // read without polluting the effect's dependency array.
  const abandonedDataRef = useRef(null);
  useEffect(() => { abandonedDataRef.current = abandonedData; }, [abandonedData]);

  // Lazy-load abandoned data when user opens the Abandoned tab, or refresh
  // when the time window changes. Stale data is kept visible during refresh.
  useEffect(() => {
    if (view !== 'abandoned') return;
    if (!currentUser || currentUser.role !== 'admin') return;
    loadAbandonedCheckouts(abandonedWindowDays, { background: abandonedDataRef.current != null });
  }, [view, abandonedWindowDays, currentUser, loadAbandonedCheckouts]);

  // Load users separately (only once)
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const user = await User.me();
        setCurrentUser(user);

        if (!user || user.role !== 'admin') {
          setIsLoading(false);
          return;
        }

        // Fetch users via User.list() which handles authorization
        const userData = await User.list();
        const usersMap = new Map(userData.map(u => [u.email, {
          name: u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email,
          photo: u.profile_photo
        }]));
        setUserMap(usersMap);
        
        // Load ready-for-push carts and first page of other carts
        await Promise.all([loadReadyForPushCarts(), loadCarts(1)]);
      } catch (error) {
        console.error('Error loading initial data:', error);
        setIsLoading(false);
      }
    };

    loadUsers();
  }, []);

  // Real-time subscription to abandoned_trips table for live updates
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin') return;

    const setupRealtimeSubscription = async () => {
      const { supabase } = await import('../lib/supabase');
      
      console.log('🔴 Setting up real-time subscription for abandoned_trips table');
      
      // Subscribe to all changes on abandoned_trips table
      const channel = supabase
        .channel('abandoned_trips_changes')
        .on(
          'postgres_changes',
          {
            event: '*', // Listen to all events: INSERT, UPDATE, DELETE
            schema: 'public',
            table: 'abandoned_trips'
          },
          (payload) => {
            console.log('🔴 Real-time update received:', payload.eventType, payload);
            // Debounce refresh to avoid rapid full refreshes while user is on the page
            if (realtimeRefreshTimeoutRef.current) {
              clearTimeout(realtimeRefreshTimeoutRef.current);
            }
            realtimeRefreshTimeoutRef.current = setTimeout(() => {
              loadReadyForPushCarts({ background: true });
              loadCarts(currentPageRef.current, searchTermRef.current, { background: true });
            }, 250);
          }
        )
        .subscribe((status) => {
          console.log('🔴 Real-time subscription status:', status);
        });

      return () => {
        console.log('🔴 Cleaning up real-time subscription');
        if (realtimeRefreshTimeoutRef.current) {
          clearTimeout(realtimeRefreshTimeoutRef.current);
          realtimeRefreshTimeoutRef.current = null;
        }
        supabase.removeChannel(channel);
      };
    };

    const cleanupPromise = setupRealtimeSubscription();

    // Cleanup subscription when component unmounts or user changes
    return () => {
      cleanupPromise.then(cleanup => cleanup && cleanup());
    };
  }, [currentUser, loadCarts, loadReadyForPushCarts]); // Stable subscription; uses refs for currentPage/searchTerm

  // When page changes, load new data
  useEffect(() => {
    if (currentUser && currentUser.role === 'admin' && currentPage > 0) {
      loadCarts(currentPage, searchTerm);
    }
  }, [currentPage, currentUser, loadCarts]); // explicit deps (no stale closures)

  const handleSearch = () => {
    if (currentUser && currentUser.role === 'admin') {
      setCurrentPage(1);
      loadCarts(1, searchTerm);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="w-8 h-8 animate-pulse text-white mx-auto mb-4" />
          <p className="text-white/60 font-normal">Loading carts...</p>
        </div>
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-light text-white mb-1">Access Denied</h2>
          <p className="text-white/60 text-sm font-normal">Administrator access required.</p>
        </div>
      </div>
    );
  }

  // Show all carts regardless of status (search is server-side)
  const paginatedCarts = carts;

  // Calculate cart total - use persisted pricing_snapshot if available (credits-first accurate)
  // Falls back to recalculation for older carts without snapshot
  const calculateCartTotal = (cart) => {
    // PRIORITY 1: Use persisted pricing_snapshot (most accurate, credits-first applied)
    const pricingSnapshot = cart.flight_data?.pricing_snapshot;
    if (pricingSnapshot?.grand_total) {
      return parseFloat(pricingSnapshot.grand_total);
    }
    
    // PRIORITY 2: Use persisted total_amount if it reflects pricing updates
    // This is set when credits are applied in Review.jsx
    if (cart.total_amount && cart.flight_data?.credits_total > 0) {
      return parseFloat(cart.total_amount);
    }
    
    // FALLBACK: Recalculate for older carts - MUST apply credits-first rule
    const flightBasePrice = parseFloat(cart.flight_data?.total_amount || 0);
    const stayPrice = cart.stay_data ? parseFloat(cart.stay_data.totalAmount || cart.stay_data.total_amount || 0) : 0;
    const creditsTotal = parseFloat(cart.flight_data?.credits_total || 0);
    
    // CRITICAL: Credits-first rule - credits apply ONLY to flight base fare, not hotel
    const flightAfterCredits = Math.max(0, flightBasePrice - creditsTotal);
    const feeBase = flightAfterCredits + stayPrice; // Add hotel back for fee calculation
    
    // Service fee WAIVED for flight+hotel packages; otherwise calculated on reduced flight + hotel
    const isFlightHotel = stayPrice > 0;
    const serviceFee = isFlightHotel ? 0 : calculateServiceFee(feeBase, isDeferaCapitalEffective(cart));
    const subtotalAfterCredits = feeBase + serviceFee;
    const holdRate = isDeferaCapitalEffective(cart) ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE;
    const holdFee = subtotalAfterCredits * holdRate;
    
    return subtotalAfterCredits + holdFee;
  };

  // Get credits info from cart
  const getCreditsInfo = (cart) => {
    const credits = cart.flight_data?.credits;
    const creditsTotal = cart.flight_data?.credits_total || 0;
    if (!credits || credits.length === 0 || creditsTotal === 0) return null;
    return {
      count: credits.length,
      total: creditsTotal,
      credits: credits
    };
  };

  // Seat-map selections saved while the customer was in checkout. Two shapes
  // exist: `seat_selections` ({ segment_0: { '14A': {…} } }, written live from
  // the Select Seats step) and flight_data.seat_selections_detail (the
  // booking-ready shape) — read whichever is present.
  const getSeatInfo = (cart) => {
    const source = (cart.seat_selections && Object.keys(cart.seat_selections).length > 0)
      ? cart.seat_selections
      : cart.flight_data?.seat_selections_detail;
    if (!source || typeof source !== 'object') return null;
    const designators = [];
    let total = 0;
    Object.values(source).forEach((segmentSeats) => {
      if (!segmentSeats || typeof segmentSeats !== 'object') return;
      Object.entries(segmentSeats).forEach(([designator, sel]) => {
        designators.push(designator);
        total += parseFloat(sel?.price?.amount) || 0;
      });
    });
    if (designators.length === 0) return null;
    const shown = designators.slice(0, 12);
    const overflow = designators.length - shown.length;
    return {
      count: designators.length,
      label: shown.join(', ') + (overflow > 0 ? ` +${overflow} more` : ''),
      total,
    };
  };

  const getStatusBadge = (status) => {
    const styles = {
      in_cart: 'bg-brand-900/40 text-white/75 border-brand-700/45',
      confirmed: 'bg-green-500/10 text-green-400 border-green-500/30',
      canceled: 'bg-red-500/10 text-red-400 border-red-500/30',
      ready_for_push: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/35',
    };
    const labels = {
      in_cart: 'In Cart',
      confirmed: 'Confirmed',
      canceled: 'Canceled',
      ready_for_push: 'Ready',
    };
    return (
      <Badge className={`text-[9px] sm:text-[10px] font-normal px-1.5 py-0.5 border ${styles[status] || styles.in_cart}`}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getStepBadge = (step) => {
    const colors = {
      review: 'bg-brand-900/35 text-white/70 border-brand-800/45',
      passenger_details: 'bg-brand-800/25 text-brand-300 border-brand-700/40',
      payment_initiated: 'bg-brand-500/15 text-brand-300 border-brand-500/35',
      completed: 'bg-green-500/10 text-green-400 border-green-500/30',
    };
    const labels = {
      review: 'Review',
      passenger_details: 'Passengers',
      payment_initiated: 'Payment',
      completed: 'Done',
    };
    return (
      <Badge className={`text-[9px] sm:text-[10px] px-1.5 py-0.5 font-normal border ${colors[step] || colors.review}`}>
        {labels[step] || step}
      </Badge>
    );
  };

  // CRITICAL: Source of truth for "when was the customer last on this cart" is
  // `last_activity`. We previously fell back to `updated_at` when `last_activity`
  // was null, but `updated_at` is bumped by a DB trigger on every UPDATE
  // (including admin status changes, server-side backfills, push-to-trips).
  // That made admin actions look like recent customer activity. We now fall
  // back only to `created_at`, which at worst shows a stale-but-not-misleading
  // value for legacy rows that were created before `last_activity` was wired up.
  const getActivityDate = (cart) => {
    const dateString = cart.last_activity || cart.created_at;
    if (!dateString) return null;
    try {
      return parseISO(dateString);
    } catch (error) {
      console.error('Error parsing timestamp:', error, { dateString, cartId: cart.id });
      return null;
    }
  };

  const renderTimestamp = (cart) => {
    const date = getActivityDate(cart);
    if (!date) return 'N/A';
    return formatDistanceToNow(date, { addSuffix: true })
      .replace('about ', '')
      .replace('less than a minute', 'now')
      .replace(' minutes', 'm')
      .replace(' minute', 'm')
      .replace(' hours', 'h')
      .replace(' hour', 'h')
      .replace(' days', 'd')
      .replace(' day', 'd')
      .replace(' months', 'mo')
      .replace(' month', 'mo')
      .replace(' years', 'y')
      .replace(' year', 'y');
  };

  // Absolute timestamp for the hover tooltip — useful when the relative
  // form ("3h") is suspect, admin can hover to verify the actual time.
  const renderTimestampTitle = (cart) => {
    const date = getActivityDate(cart);
    if (!date) return 'No activity recorded';
    return `Last activity: ${format(date, 'MMM d, yyyy h:mm a')}`;
  };

  const getRouteDisplay = (cart) => {
    try {
      // Extract city names from flight_data segments (most reliable source)
      const firstSlice = cart.flight_data?.slices?.[0];
      if (firstSlice && firstSlice.segments && firstSlice.segments.length > 0) {
        const firstSegment = firstSlice.segments[0];
        const lastSegment = firstSlice.segments[firstSlice.segments.length - 1];
        
        // Get IATA codes from segments
        const originCode = firstSegment.origin?.iata_code || firstSegment.origin?.city_name;
        const destinationCode = lastSegment.destination?.iata_code || lastSegment.destination?.city_name;
        
        // Convert to city names (getCityName handles codes and city names)
        const originCity = getCityName(originCode) || 'Unknown';
        const destinationCity = getCityName(destinationCode) || 'Unknown';
        
        return `${originCity} → ${destinationCity}`;
      }
      
      // Fallback: Use departure_city and arrival_city (getCityName handles both codes and names)
      if (cart.departure_city && cart.arrival_city) {
        const originCity = getCityName(cart.departure_city);
        const destinationCity = getCityName(cart.arrival_city);
        return `${originCity} → ${destinationCity}`;
      }
      
      // Last resort fallback
      return 'Unknown Route';
    } catch (error) {
      console.error('Error extracting route from flight data:', error);
      return 'Unknown Route';
    }
  };

  const handleCopyUrl = async (url, cartId) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(cartId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };


  return (
    <div className="min-h-[70vh] pb-12 relative">
      <RoyalAmbientBackground />
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-6 relative z-10">
        {/* Header strip — same compact pattern as the Dashboard. Title +
            subtitle on the left, inline metric ribbon on the right with
            hairline dividers. Drops the marketing-style icon next to the
            heading and folds the previous 4-card stat grid into a single
            text ribbon, killing about 100px of vertical chrome. */}
        <motion.div
          className="mb-5 mt-2 md:mt-4"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-6">
            <div className="min-w-0">
              <h1 className="text-3xl md:text-[32px] font-light text-white tracking-tight leading-none">
                Carts
              </h1>
              <p className="text-white/60 text-[12.5px] font-normal mt-2.5 leading-none">
                Manage abandoned trips and push them to bookings.
              </p>
            </div>

            <div className="flex items-center gap-3 md:gap-4 text-xs flex-wrap">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-3 h-3 text-[hsl(228,35%,58%)]/80" />
                <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal">Total</span>
                <span className="text-white/90 tabular-nums font-normal">{totalCount}</span>
              </div>
              <span className="w-px h-3 bg-white/10" aria-hidden="true" />
              <div className="flex items-center gap-2">
                <TrendingUp className="w-3 h-3 text-[hsl(228,35%,58%)]/80" />
                <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal">Value</span>
                <span className="text-white/90 tabular-nums font-normal">${Math.round(totalValue).toLocaleString()}</span>
              </div>
              {(stageCounts.review > 0 || stageCounts.passenger_details > 0 || stageCounts.payment_initiated > 0) && (
                <>
                  <span className="w-px h-3 bg-white/10" aria-hidden="true" />
                  <div className="flex items-center gap-2" title="Carts that reached the Review screen">
                    <Eye className="w-3 h-3 text-white/40" />
                    <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal">Review</span>
                    <span className="text-white/85 tabular-nums font-normal">{stageCounts.review}</span>
                  </div>
                  <div className="flex items-center gap-2" title="Carts that reached the Passenger Details step">
                    <UsersIcon className="w-3 h-3 text-white/40" />
                    <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal">Pax</span>
                    <span className="text-white/85 tabular-nums font-normal">{stageCounts.passenger_details}</span>
                  </div>
                  <div className="flex items-center gap-2" title="Carts that initiated payment">
                    <CreditCard className="w-3 h-3 text-white/40" />
                    <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal">Pay</span>
                    <span className="text-white/85 tabular-nums font-normal">{stageCounts.payment_initiated}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>

        {/* Tab toggle: In Cart (default) vs. Abandoned (Defera Capital
            silent-abandon detector). The Abandoned tab is a separate dataset
            (Stripe-enriched) loaded on demand — see /api/admin/abandoned-checkouts. */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.07 }}
          className="mb-5"
        >
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-brand-900/30 border border-brand-800/45">
            <button
              type="button"
              onClick={() => setView('in_cart')}
              className={cn(
                'px-4 py-1.5 rounded-full text-xs font-normal transition-colors border',
                view === 'in_cart' ? DASHBOARD_TAB_ACTIVE : cn(DASHBOARD_TAB_INACTIVE, 'border-transparent'),
              )}
            >
              In Cart
            </button>
            <button
              type="button"
              onClick={() => setView('abandoned')}
              className={cn(
                'px-4 py-1.5 rounded-full text-xs font-normal transition-colors border inline-flex items-center gap-1.5',
                view === 'abandoned' ? DASHBOARD_TAB_ACTIVE : cn(DASHBOARD_TAB_INACTIVE, 'border-transparent'),
              )}
              title="Defera Capital checkouts that started but never landed a booking"
            >
              <AlertTriangle className="w-3 h-3" />
              Abandoned
              {abandonedData?.summary?.hardAbandonCount > 0 && (
                <span className="ml-1 text-[10px] tabular-nums text-red-300 bg-red-500/15 border border-red-500/25 rounded-full px-1.5">
                  {abandonedData.summary.hardAbandonCount}
                </span>
              )}
            </button>
          </div>
        </motion.div>

        {view === 'in_cart' && (
        <>
        {/* Ready to Push - Miles bookings awaiting admin */}
        {readyForPushCarts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className={cn(CART_SECTION_CARD, 'mb-5 border-cyan-500/20')}
          >
            <SectionBand
              icon={Send}
              title="Ready to Push"
              subtitle="Miles bookings awaiting admin push"
              rightSlot={(
                <span className="text-[10px] font-medium text-cyan-200 bg-cyan-500/15 border border-cyan-500/30 rounded-full px-2 py-0.5 tabular-nums">
                  {readyForPushCarts.length}
                </span>
              )}
            />
            <div className={BRAND_LIST_DIVIDER}>
                {readyForPushCarts.map((cart, index) => {
                  const user = userMap.get(cart.user_email);
                  return (
                    <Link to={createPageUrl(`EditCart?id=${cart.id}`)} key={cart.id}>
                      <motion.div
                        className="p-4 sm:px-5 hover:bg-brand-900/30 transition-colors cursor-pointer"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02 }}
                      >
                        {/* flex-wrap on mobile: identity stays on line one, price/badges/time
                            wrap below instead of clipping inside the overflow-hidden card. */}
                        <div className="flex items-center gap-3 gap-y-2 sm:gap-4 flex-wrap sm:flex-nowrap">
                          <div className={cartLogoShell}>
                            {cart.flight_data?.slices?.[0]?.segments?.[0]?.marketing_carrier?.logo_symbol_url ? (
                              <img
                                src={cart.flight_data.slices[0].segments[0].marketing_carrier.logo_symbol_url}
                                alt={cart.marketing_airline}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <Plane className="w-5 h-5 text-white/60" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-normal text-white truncate">
                              {user?.name || cart.user_email || 'Guest'}
                              <VipBadge email={cart.user_email || null} className="ml-1.5 align-middle" />
                            </p>
                            <div className="flex items-center gap-2 text-xs text-white/60 font-normal">
                              <span className="truncate">{getRouteDisplay(cart)}</span>
                              <span>•</span>
                              <span className="truncate">{cart.marketing_airline}</span>
                            </div>
                          </div>
                          <div className="text-sm text-white font-normal whitespace-nowrap">
                            ${calculateCartTotal(cart).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          {cart.flight_data?.promo_applied && (
                            <Badge className="bg-brand-500/10 text-brand-300 border-brand-500/20 text-[9px] font-normal px-1.5 py-0.5">
                              Private Sale
                            </Badge>
                          )}
                          {getStatusBadge(cart.status)}
                          <div className="flex items-center gap-1 text-xs text-white/60 font-normal sm:min-w-20" title={renderTimestampTitle(cart)}>
                            <Clock className="w-3 h-3" />
                            <span className="truncate">{renderTimestamp(cart)}</span>
                          </div>
                        </div>
                      </motion.div>
                    </Link>
                  );
                })}
            </div>
          </motion.div>
        )}

        {/* Slim pill search — Enter to submit, no separate Search button.
            Stage counts moved into the header metric ribbon, so the previous
            4-card stat grid was removed entirely. */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-5"
        >
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[hsl(228,35%,58%)]/70 pointer-events-none" />
            <Input
              type="text"
              placeholder="Search by user, route, airline, email, phone, or passenger name…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              className={cn(DASHBOARD_FILTER_INPUT, 'pl-9 pr-4 h-10 rounded-full text-xs font-normal')}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className={CART_SECTION_CARD}>
          <SectionBand
            icon={ShoppingCart}
            title="Active carts"
            subtitle={searchTerm ? `Filtered · ${paginatedCarts.length} on this page` : `${totalCount.toLocaleString()} total`}
          />
          <div className={BRAND_LIST_DIVIDER}>
            {paginatedCarts.length > 0 ? (
              paginatedCarts.map((cart, index) => {
                const user = userMap.get(cart.user_email);
                return (
                  <Link to={createPageUrl(`EditCart?id=${cart.id}`)} key={cart.id}>
                    <motion.div
                      className="p-4 sm:px-5 hover:bg-brand-900/30 transition-colors cursor-pointer"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}>
                      
                      {/* Desktop Layout */}
                      <div className="hidden md:block">
                        <div className="flex items-center gap-4 mb-2">
                          <div className={cartLogoShell}>
                            {cart.flight_data?.slices?.[0]?.segments?.[0]?.marketing_carrier?.logo_symbol_url ? (
                              <img 
                                src={cart.flight_data.slices[0].segments[0].marketing_carrier.logo_symbol_url} 
                                alt={cart.marketing_airline}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <Plane className="w-5 h-5 text-white/60" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-normal text-white truncate">
                              {user?.name || cart.user_email || 'Guest'}
                              <VipBadge email={cart.user_email || null} className="ml-1.5 align-middle" />
                            </p>
                            <div className="flex items-center gap-2 text-xs text-white/60 font-normal">
                              <Plane className="w-3 h-3" />
                              <span className="truncate">
                                {getRouteDisplay(cart)}
                              </span>
                              <span>•</span>
                              <span className="truncate">{cart.marketing_airline}</span>
                              {cart.stay_data && (
                                <span className="truncate"> + Stay</span>
                              )}
                            </div>
                          </div>
                          <div className="text-sm text-white font-medium tabular-nums">
                            <span>${calculateCartTotal(cart).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {cart.flight_data?.promo_applied && (
                              <Badge className="bg-brand-500/10 text-brand-300 border-brand-500/20 text-[9px] font-normal px-1.5 py-0.5">
                                Private Sale
                              </Badge>
                            )}
                            {getStatusBadge(cart.status)}
                            {cart.step_reached && getStepBadge(cart.step_reached)}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-white/60 font-normal min-w-20" title={renderTimestampTitle(cart)}>
                            <Clock className="w-3 h-3" />
                            <span className="truncate">{renderTimestamp(cart)}</span>
                          </div>
                        </div>
                        
                        {/* Hotel Info if present - Desktop */}
                        {cart.stay_data && (
                          <div className={cn(cartMetaChip, 'ml-14 mb-2')}>
                            🏨 {cart.stay_data.name} • {cart.stay_data.nights} night{cart.stay_data.nights !== 1 ? 's' : ''} • ${(cart.stay_data.totalAmount || cart.stay_data.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        )}
                        
                        {/* Credits Info if present - Desktop */}
                        {getCreditsInfo(cart) && (
                          <div className={cn(cartMetaChip, 'ml-14 mb-2 text-green-300/90 bg-green-500/10 border-green-500/30')}>
                            🎁 {getCreditsInfo(cart).count} Credit{getCreditsInfo(cart).count !== 1 ? 's' : ''} Applied: -${getCreditsInfo(cart).total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        )}

                        {/* Seat selections from the live seat map - Desktop */}
                        {getSeatInfo(cart) && (
                          <div className={cn(cartMetaChip, 'ml-14 mb-2 text-brand-300/90 bg-brand-500/10 border-brand-500/35')}>
                            💺 {getSeatInfo(cart).count} Seat{getSeatInfo(cart).count !== 1 ? 's' : ''} Selected: {getSeatInfo(cart).label}
                            {getSeatInfo(cart).total > 0
                              ? ` • $${getSeatInfo(cart).total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : ' • Free'}
                          </div>
                        )}
                        
                        {/* Passenger Info - Desktop */}
                        {cart.passengers && cart.passengers.length > 0 ? (
                          <div className={cn(cartMetaChip, 'ml-14 mb-2 text-emerald-200/90 bg-emerald-500/10 border-emerald-500/30')}>
                            👥 {cart.passengers.length} {cart.passengers.length === 1 ? 'Passenger' : 'Passengers'}: {cart.passengers.map(p => `${p.first_name || p.given_name || ''} ${p.last_name || p.family_name || ''}`.trim()).filter(Boolean).join(', ')}
                            {cart.passengers[0]?.email && ` • ${cart.passengers[0].email}`}
                          </div>
                        ) : cart.step_reached === 'passenger_details_completed' || cart.step_reached === 'payment_initiated' || cart.payment_link ? (
                          <div className={cn(cartMetaChip, 'ml-14 mb-2 text-brand-200/90 bg-brand-500/10 border-brand-500/35')}>
                            ⚠️ No passenger info saved
                          </div>
                        ) : null}
                        
                        {/* Payment Info Row */}
                        {cart.payment_link && (
                          <div className="flex items-center gap-4 ml-14 text-xs">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  window.open(cart.payment_link, '_blank');
                                }}
                                className="flex items-center gap-1.5 px-2 py-1 bg-brand-500/20 hover:bg-brand-500/30 border border-brand-500/30 rounded-md text-brand-300 transition-colors"
                              >
                                <ExternalLink className="w-3 h-3" />
                                <span className="font-normal">{ic.cartsPartnerShortLabel}</span>
                              </button>
                              {(cart.flight_data?.defera_booking_reference || cart.booking_reference) && (
                                <span className="px-2 py-1 bg-brand-900/35 border border-brand-800/40 rounded-md text-white/80 font-mono text-xs">
                                  Ref: {cart.flight_data?.defera_booking_reference || cart.booking_reference}
                                </span>
                              )}
                              <PaymentProcessorBadge flightData={cart.flight_data} />
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleCopyUrl(cart.payment_link, cart.id);
                                }}
                                className="p-1 hover:bg-white/10 rounded transition-colors"
                                title="Copy payment URL"
                              >
                                {copiedId === cart.id ? (
                                  <Check className="w-3.5 h-3.5 text-green-400" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5 text-white/60" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Mobile Layout - Compact */}
                      <div className="md:hidden">
                        {/* Top Row - User/Airline and Price/Status */}
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={cn(cartLogoShell, 'w-12 h-12')}>
                              {cart.flight_data?.slices?.[0]?.segments?.[0]?.marketing_carrier?.logo_symbol_url ? (
                                <img 
                                  src={cart.flight_data.slices[0].segments[0].marketing_carrier.logo_symbol_url} 
                                  alt={cart.marketing_airline}
                                  className="w-full h-full object-contain"
                                />
                              ) : (
                                <Plane className="w-6 h-6 text-white/60" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-normal text-white truncate">
                                {user?.name || cart.user_email || 'Guest'}
                              <VipBadge email={cart.user_email || null} className="ml-1.5 align-middle" />
                              </p>
                              <p className="text-xs text-white/60 font-normal truncate">
                                {cart.marketing_airline}
                                {cart.stay_data && ' + Stay'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Right Side - Price, Status, Stage */}
                          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                            <div className="text-sm font-normal text-white whitespace-nowrap">
                              ${calculateCartTotal(cart).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            <div className="flex items-center gap-1.5">
                              {cart.flight_data?.promo_applied && (
                                <Badge className="bg-brand-500/10 text-brand-300 border-brand-500/20 text-[9px] font-normal px-1.5 py-0.5">
                                  Private Sale
                                </Badge>
                              )}
                              {getStatusBadge(cart.status)}
                              {cart.step_reached && getStepBadge(cart.step_reached)}
                            </div>
                          </div>
                        </div>
                        
                        {/* Route and Time on Same Row */}
                        <div className="flex items-center justify-between gap-2 text-sm text-white font-normal mb-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Plane className="w-4 h-4 text-white/60 flex-shrink-0" />
                            <span className="truncate">{getRouteDisplay(cart)}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-white/60 font-normal flex-shrink-0" title={renderTimestampTitle(cart)}>
                            <Clock className="w-3 h-3" />
                            <span>{renderTimestamp(cart)}</span>
                          </div>
                        </div>
                        
                        {/* Hotel Info if present */}
                        {cart.stay_data && (
                          <div className={cn(cartMetaChip, 'mb-2 w-full')}>
                            🏨 {cart.stay_data.name} • {cart.stay_data.nights} night{cart.stay_data.nights !== 1 ? 's' : ''} • ${(cart.stay_data.totalAmount || cart.stay_data.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        )}
                        
                        {/* Credits Info if present - Mobile */}
                        {getCreditsInfo(cart) && (
                          <div className="text-xs text-green-400/80 font-normal bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2 mb-2">
                            🎁 {getCreditsInfo(cart).count} Credit{getCreditsInfo(cart).count !== 1 ? 's' : ''} Applied: -${getCreditsInfo(cart).total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        )}

                        {/* Seat selections from the live seat map - Mobile */}
                        {getSeatInfo(cart) && (
                          <div className="text-xs text-brand-300/90 font-normal bg-brand-500/10 border border-brand-500/20 rounded-lg px-3 py-2 mb-2">
                            💺 {getSeatInfo(cart).count} Seat{getSeatInfo(cart).count !== 1 ? 's' : ''} Selected: {getSeatInfo(cart).label}
                            {getSeatInfo(cart).total > 0
                              ? ` • $${getSeatInfo(cart).total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : ' • Free'}
                          </div>
                        )}
                        
                        {/* Passenger Info - Mobile */}
                        {cart.passengers && cart.passengers.length > 0 ? (
                          <div className="text-xs text-white/80 font-normal bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 mb-2">
                            👥 {cart.passengers.length} {cart.passengers.length === 1 ? 'Passenger' : 'Passengers'}: {cart.passengers.map(p => `${p.first_name || p.given_name || ''} ${p.last_name || p.family_name || ''}`.trim()).filter(Boolean).join(', ')}
                            {cart.passengers[0]?.email && (
                              <div className="text-white/60 mt-1">{cart.passengers[0].email}</div>
                            )}
                          </div>
                        ) : cart.step_reached === 'passenger_details_completed' || cart.step_reached === 'payment_initiated' || cart.payment_link ? (
                          <div className="text-xs text-brand-200/80 font-normal bg-brand-500/10 border border-brand-500/30 rounded-lg px-3 py-2 mb-2">
                            ⚠️ No passenger info saved
                          </div>
                        ) : null}
                        
                        {/* Payment Info - Mobile */}
                        {cart.payment_link && (
                          <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-white/5">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  window.open(cart.payment_link, '_blank');
                                }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500/20 hover:bg-brand-500/30 border border-brand-500/30 rounded-lg text-brand-300 transition-colors text-xs flex-1 justify-center"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              <span className="font-normal">{ic.cartsOpenPartnerLink}</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleCopyUrl(cart.payment_link, cart.id);
                                }}
                              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                              >
                                {copiedId === cart.id ? (
                                  <Check className="w-4 h-4 text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4 text-white/60" />
                                )}
                              </button>
                            </div>
                        )}
                        
                            {(cart.flight_data?.defera_booking_reference || cart.booking_reference) && (
                          <div className="mt-2 text-center">
                            <span className="inline-block px-2 py-1 bg-brand-900/35 border border-brand-800/40 rounded text-white/60 font-mono text-[10px]">
                                Ref: {cart.flight_data?.defera_booking_reference || cart.booking_reference}
                            </span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </Link>
                );
              })
            ) : (
              <div className="p-8 text-center text-white/60 font-normal">
                {searchTerm ? 'No carts match your search.' : 'No abandoned carts found.'}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-brand-800/35 flex items-center justify-between bg-brand-900/20">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || isLoading}
                className="h-8 px-3 text-xs text-white/70 hover:text-white hover:bg-white/[0.06] rounded-full font-normal"
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1.5" />
                Previous
              </Button>
              <div className="text-[11px] text-white/45 font-normal tabular-nums">
                Page <span className="text-white/85">{currentPage}</span> of {totalPages}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || isLoading}
                className="h-8 px-3 text-xs text-white/70 hover:text-white hover:bg-white/[0.06] rounded-full font-normal"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          )}
          </motion.div>
        </>
        )}

        {view === 'abandoned' && (
          <AbandonedCheckoutsView
            data={abandonedData}
            loading={abandonedLoading}
            error={abandonedError}
            windowDays={abandonedWindowDays}
            onWindowDaysChange={setAbandonedWindowDays}
            classification={abandonedClassification}
            onClassificationChange={setAbandonedClassification}
            bucket={abandonedBucket}
            onBucketChange={setAbandonedBucket}
            onRefresh={() => loadAbandonedCheckouts(abandonedWindowDays, { background: abandonedData != null })}
          />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// AbandonedCheckoutsView — Defera Capital silent-abandon panel.
//
// This is the UI side of /api/admin/abandoned-checkouts, which itself mirrors
// scripts/audit-abandoned-checkouts.mjs. Three jobs:
//   1) Surface the dollars-at-risk and case count at a glance.
//   2) Categorize each abandon by primary_reason.bucket so admins can see
//      whether the failures are customer-side (bank/card), Stripe-side
//      (Radar), or our system (idempotency_error / orphan deposit).
//   3) Give a one-click jump to the Stripe invoice + the customer's contact.
// =============================================================================
function bucketStyle(bucket) {
  if (bucket === 'customer') return { Icon: CreditCard, label: 'Customer', ring: 'border-brand-500/30 bg-brand-500/10', text: 'text-brand-300' };
  if (bucket === 'stripe')   return { Icon: ShieldAlert, label: 'Stripe',   ring: 'border-brand-500/30 bg-brand-500/10', text: 'text-brand-300' };
  if (bucket === 'system')   return { Icon: Server,      label: 'System',   ring: 'border-red-500/30 bg-red-500/10',     text: 'text-red-300' };
  return { Icon: HelpCircle, label: 'Unknown', ring: 'border-white/15 bg-white/5', text: 'text-white/55' };
}

// "Thu, Jun 27 2026" — weekday prefix makes travel dates much faster to read
// at a glance than "2026-07-10 08:40". Pure date (no time) since the time
// component on flight dates here is the local departure clock, not useful in
// an admin scan view.
function fmtTripDate(value) {
  if (!value) return null;
  try {
    const dateOnly = String(value).slice(0, 10);
    const parsed = parseISO(dateOnly);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return format(parsed, 'EEE, MMM d yyyy');
  } catch {
    return String(value);
  }
}

function fmtTimestampLong(value) {
  if (!value) return '';
  try { return format(parseISO(value), 'EEE, MMM d yyyy h:mm a'); }
  catch { return String(value); }
}

function AbandonedCheckoutsView({
  data,
  loading,
  error,
  windowDays,
  onWindowDaysChange,
  classification,
  onClassificationChange,
  bucket,
  onBucketChange,
  onRefresh,
}) {
  const summary = data?.summary;
  const allCases = data?.cases || [];
  const filteredCases = allCases.filter((c) => {
    if (classification === 'hard' && c.classification !== 'hard_abandon') return false;
    if (classification === 'soft' && c.classification !== 'soft_recovery') return false;
    if (bucket !== 'all' && (c.primary_reason?.bucket || 'unknown') !== bucket) return false;
    return true;
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="space-y-4"
    >
      {/* Top control row: time window + refresh + revenue-at-risk callout */}
      <div className={cn(CART_SECTION_CARD, 'flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-3')}>
        <div className="flex items-center gap-3 flex-wrap text-xs">
          <span className="text-white/40 uppercase tracking-[0.14em] text-[10px] font-normal">Window</span>
          <div className="inline-flex items-center gap-1 p-0.5 rounded-full bg-white/[0.04] border border-white/10">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => onWindowDaysChange(d)}
                className={`px-3 py-1 rounded-full text-[11px] font-normal transition-colors ${
                  windowDays === d ? 'bg-white/10 text-white' : 'text-white/55 hover:text-white/85'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-normal text-white/70 hover:text-white hover:bg-white/[0.06] border border-white/10 disabled:opacity-40"
            title="Re-fetch (also hits Stripe for current invoice state)"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {summary && (
          <div className="flex items-center gap-3 md:gap-4 text-xs flex-wrap">
            <div className="flex items-center gap-2" title="Hard abandons: no booking landed and customer hasn't successfully booked anything else in the window.">
              <AlertTriangle className="w-3 h-3 text-red-300" />
              <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal">Hard</span>
              <span className="text-red-300 tabular-nums font-normal">{summary.hardAbandonCount}</span>
            </div>
            <span className="w-px h-3 bg-white/10" aria-hidden="true" />
            <div className="flex items-center gap-2" title="Revenue at risk: sum of total trip cost across hard abandons (not just deposits).">
              <TrendingUp className="w-3 h-3 text-red-300" />
              <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal">At Risk</span>
              <span className="text-red-300 tabular-nums font-normal">${(summary.revenueAtRiskCents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
            </div>
            <span className="w-px h-3 bg-white/10" aria-hidden="true" />
            <div className="flex items-center gap-2" title="Soft recoveries: same user booked something successfully in the window — no action needed.">
              <Check className="w-3 h-3 text-emerald-300" />
              <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal">Soft</span>
              <span className="text-emerald-300 tabular-nums font-normal">{summary.softRecoveryCount}</span>
            </div>
          </div>
        )}
      </div>

      {/* Bucket / classification filter chips */}
      {summary && (
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal mr-1">Show</span>
          {[
            { id: 'hard', label: 'Hard abandons', count: summary.hardAbandonCount },
            { id: 'soft', label: 'Soft recoveries', count: summary.softRecoveryCount },
            { id: 'all',  label: 'All', count: summary.hardAbandonCount + summary.softRecoveryCount },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onClassificationChange(opt.id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-normal border transition-colors ${
                classification === opt.id
                  ? 'bg-white/[0.08] border-white/20 text-white'
                  : 'bg-white/[0.02] border-white/10 text-white/55 hover:text-white/85'
              }`}
            >
              {opt.label} <span className="tabular-nums opacity-70">({opt.count})</span>
            </button>
          ))}

          <span className="w-px h-3 bg-white/10 mx-2" aria-hidden="true" />
          <span className="text-white/35 uppercase tracking-[0.14em] text-[10px] font-normal mr-1">Cause</span>
          {[
            { id: 'all', label: 'All' },
            { id: 'customer', label: 'Customer (card/bank)' },
            { id: 'stripe', label: 'Stripe (Radar)' },
            { id: 'system', label: 'Our system' },
            { id: 'unknown', label: 'Unknown' },
          ].map((opt) => {
            const count = opt.id === 'all'
              ? (summary.bucketCounts.customer + summary.bucketCounts.stripe + summary.bucketCounts.system + summary.bucketCounts.unknown)
              : (summary.bucketCounts[opt.id] || 0);
            const isActive = bucket === opt.id;
            const isEmpty = opt.id !== 'all' && count === 0;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => onBucketChange(opt.id)}
                disabled={isEmpty}
                className={`px-2.5 py-1 rounded-full text-[11px] font-normal border transition-colors ${
                  isActive
                    ? 'bg-white/[0.08] border-white/20 text-white'
                    : 'bg-white/[0.02] border-white/10 text-white/55 hover:text-white/85 disabled:opacity-30 disabled:cursor-not-allowed'
                }`}
              >
                {opt.label} <span className="tabular-nums opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Body */}
      {error && (
        <div className="rounded-2xl bg-red-500/[0.06] border border-red-500/20 px-4 py-3 text-sm text-red-300 font-normal">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className={cn(CART_SECTION_CARD, 'p-12 text-center')}>
          <RefreshCw className="w-5 h-5 animate-spin text-white/60 mx-auto mb-3" />
          <p className="text-white/60 text-xs font-normal">Scanning pending checkouts and Stripe…</p>
        </div>
      )}

      {data && filteredCases.length === 0 && (
        <div className={cn(CART_SECTION_CARD, 'p-10 text-center')}>
          <Check className="w-5 h-5 text-emerald-300 mx-auto mb-2" />
          <p className="text-white/70 text-sm font-normal">No abandoned checkouts match this filter.</p>
          <p className="text-white/60 text-[11px] font-normal mt-1">
            {classification !== 'all' || bucket !== 'all' ? 'Try a wider filter, or' : ''} expand the window above.
          </p>
        </div>
      )}

      {data && filteredCases.length > 0 && (
        <div className={cn(CART_SECTION_CARD, BRAND_LIST_DIVIDER)}>
          {filteredCases.map((c) => (
            <AbandonedCheckoutRow key={c.id} c={c} />
          ))}
        </div>
      )}
    </motion.div>
  );
}

function AbandonedCheckoutRow({ c }) {
  const isHard = c.classification === 'hard_abandon';
  const reason = c.primary_reason || { code: 'unknown', label: 'Unknown', bucket: 'unknown' };
  const { Icon, label, ring, text } = bucketStyle(reason.bucket);
  const last = c.stripe?.last_failure;
  return (
    <div className={`p-4 sm:px-5 hover:bg-brand-900/30 transition-colors ${isHard ? '' : 'opacity-75'}`}>
      <div className="flex items-start gap-4">
        {/* Airline logo (or plane fallback) */}
        <div className={cartLogoShell}>
          {c.airline_logo ? (
            <img src={c.airline_logo} alt={c.marketing_airline || ''} className="w-full h-full object-contain" />
          ) : (
            <Plane className="w-5 h-5 text-white/55" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          {/* Identity + classification + dollars */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="text-sm font-normal text-white truncate">
              {c.user_name || c.user_email || 'Guest'}
            </p>
            <span className="text-white/30">·</span>
            <span className="text-[11px] text-white/55 font-mono">{c.trip_reference}</span>
            {isHard ? (
              <Badge className="bg-red-500/15 text-red-300 border border-red-500/25 text-[9px] font-normal px-1.5 py-0.5">
                Hard abandon
              </Badge>
            ) : (
              <Badge className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[9px] font-normal px-1.5 py-0.5">
                Soft recovery
              </Badge>
            )}
            {c.deposit_paid && (
              <Badge className="bg-red-500/15 text-red-300 border border-red-500/30 text-[9px] font-normal px-1.5 py-0.5">
                ⚠ Deposit paid (orphan)
              </Badge>
            )}
          </div>

          {/* Route + dates + age */}
          <div className="flex items-center gap-2 text-xs text-white/60 font-normal flex-wrap">
            <Plane className="w-3 h-3" />
            <span className="truncate">{c.departure_city || '?'} → {c.arrival_city || '?'}</span>
            <span>·</span>
            <span>{fmtTripDate(c.departure_date)}{c.return_date ? ` → ${fmtTripDate(c.return_date)}` : ''}</span>
            <span>·</span>
            <span
              className="inline-flex items-center gap-1 text-white/45"
              title={`Snapshot: ${fmtTimestampLong(c.created_at)}\nLast updated: ${fmtTimestampLong(c.last_activity_at)}`}
            >
              <Clock className="w-3 h-3" /> {c.age_hours}h ago
            </span>
          </div>

          {/* Reason chip + Stripe inline details */}
          <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${ring} ${text} font-normal`}>
              <Icon className="w-3 h-3" />
              {label} · {reason.label}
            </span>
            {/* Server-stamped error message — authoritative when present.
                Distinct red treatment for system-bucket so it pops in scans. */}
            {c.server_error?.message && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-normal max-w-[480px] truncate ${
                  reason.bucket === 'system'
                    ? 'bg-red-500/10 border border-red-500/25 text-red-200'
                    : 'bg-white/[0.04] border border-white/10 text-white/55'
                }`}
                title={c.server_error.message}
              >
                Server: {c.server_error.message}
              </span>
            )}
            {c.stripe?.invoice_status && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-white/55 font-normal">
                Invoice: <span className="text-white/80">{c.stripe.invoice_status}</span>
                {typeof c.stripe.attempt_count === 'number' && (
                  <span className="text-white/45"> · {c.stripe.attempt_count} attempt{c.stripe.attempt_count === 1 ? '' : 's'}</span>
                )}
              </span>
            )}
            {last?.card_brand && last?.card_last4 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-white/55 font-normal">
                {last.card_brand} •••• {last.card_last4}
              </span>
            )}
            {last?.outcome_seller_message && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-white/55 font-normal max-w-[480px] truncate"
                title={last.outcome_seller_message}
              >
                {last.outcome_seller_message}
              </span>
            )}
          </div>

          {/* Customer contact + Stripe link */}
          <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px]">
            {c.user_email && (
              <a
                href={`mailto:${c.user_email}?subject=Following%20up%20on%20your%20${encodeURIComponent(c.departure_city || 'recent')}%20→%20${encodeURIComponent(c.arrival_city || 'trip')}%20booking&body=${encodeURIComponent('Hi ' + (c.user_name || '') + ',\n\nI noticed your recent booking attempt didn’t go through. ')}`}
                className="inline-flex items-center gap-1 text-white/55 hover:text-white"
                title="Send a follow-up email"
              >
                <Mail className="w-3 h-3" /> {c.user_email}
              </a>
            )}
            {c.user_phone && (
              <a href={`tel:${c.user_phone}`} className="inline-flex items-center gap-1 text-white/55 hover:text-white">
                <Phone className="w-3 h-3" /> {c.user_phone}
              </a>
            )}
            {c.stripe?.dashboard_url && (
              <a
                href={c.stripe.dashboard_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-brand-300 hover:text-brand-200"
                title="Open invoice in Stripe Dashboard"
              >
                <ExternalLink className="w-3 h-3" /> Stripe invoice
              </a>
            )}
          </div>
        </div>

        {/* Right column: dollars at risk */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0 min-w-[120px]">
          <div className="text-sm text-white font-normal tabular-nums">
            ${((c.total_amount_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-white/45 font-normal tabular-nums">
            dep ${((c.deposit_cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {c.deposit_percentage ? ` · ${c.deposit_percentage}%` : ''}
          </div>
          {c.payment_frequency && (
            <div className="text-[10px] text-white/45 font-normal capitalize">
              {String(c.payment_frequency).replace(/_/g, ' ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
